export interface Lesson {
  id: number
  title: string
  duration: string
  type:
    | 'video'
    | 'reading'
    | 'quiz'
    | 'exercise'
    | 'checklist'
    | 'challenge'
    | 'diary'
  status: 'completed' | 'current' | 'locked'
}

export interface Module {
  id: number
  title: string
  lessons: Lesson[]
  completed: number
  total: number
  status: 'completed' | 'in-progress' | 'locked'
}

export interface CourseData {
  id: number
  title: string
  subtitle: string
  progressText: string
  modulesCompleted: number
  totalModules: number
  modules: Module[]
  defaultLesson: {
    moduleId: number
    lessonId: number
    title: string
    type: 'video' | 'reading'
    description: string
    objectives: string[]
    materials: Array<{
      title: string
      type: string
      size: string
    }>
    videoPlaceholder?: string
    readingContent?: string
  }
}

export const coursesData: Record<number, CourseData> = {
  1: {
    id: 1,
    title: 'Climate Change 101: cosa dice la scienza',
    subtitle: 'Corso attivo • 3 moduli',
    progressText: '2/3 moduli completati',
    modulesCompleted: 2,
    totalModules: 3,
    modules: [
      {
        id: 1,
        title: "Il sistema climatico e l'effetto serra",
        completed: 4,
        total: 4,
        status: 'completed',
        lessons: [
          {
            id: 1,
            title: "Come funziona l'effetto serra (in 5 minuti)",
            duration: '05:00',
            type: 'video',
            status: 'completed',
          },
          {
            id: 2,
            title: 'Clima vs meteo: differenze pratiche',
            duration: '03:00',
            type: 'reading',
            status: 'completed',
          },
          {
            id: 3,
            title: 'Miti comuni sul clima',
            duration: '02:00',
            type: 'quiz',
            status: 'completed',
          },
          {
            id: 4,
            title: 'Individua 3 fonti di emissioni nella tua giornata',
            duration: '04:00',
            type: 'exercise',
            status: 'completed',
          },
        ],
      },
      {
        id: 2,
        title: 'Emissioni, CO₂e e settori chiave',
        completed: 5,
        total: 5,
        status: 'completed',
        lessons: [
          {
            id: 1,
            title: 'CO₂e spiegata bene',
            duration: '06:00',
            type: 'video',
            status: 'completed',
          },
          {
            id: 2,
            title: 'Energia, trasporti, cibo: chi pesa di più?',
            duration: '04:00',
            type: 'reading',
            status: 'completed',
          },
          {
            id: 3,
            title: 'Dove puoi ridurre subito?',
            duration: '03:00',
            type: 'checklist',
            status: 'completed',
          },
          {
            id: 4,
            title: 'Quale azione riduce di più?',
            duration: '02:00',
            type: 'quiz',
            status: 'completed',
          },
          {
            id: 5,
            title: 'Imposta un obiettivo settimanale',
            duration: '03:00',
            type: 'exercise',
            status: 'completed',
          },
        ],
      },
      {
        id: 3,
        title: 'Impatti, rischi e scenari',
        completed: 0,
        total: 4,
        status: 'in-progress',
        lessons: [
          {
            id: 1,
            title: 'Cosa cambia a +1,5°C e +2°C',
            duration: '06:00',
            type: 'video',
            status: 'current',
          },
          {
            id: 2,
            title: 'Eventi estremi: perché aumentano',
            duration: '04:00',
            type: 'reading',
            status: 'locked',
          },
          {
            id: 3,
            title: 'Impatti diretti e indiretti',
            duration: '03:00',
            type: 'quiz',
            status: 'locked',
          },
          {
            id: 4,
            title: 'Quali rischi sono più rilevanti per la tua zona?',
            duration: '03:00',
            type: 'exercise',
            status: 'locked',
          },
        ],
      },
    ],
    defaultLesson: {
      moduleId: 3,
      lessonId: 1,
      title: 'Cosa cambia a +1,5°C e +2°C',
      type: 'video',
      description:
        'Questa lezione esplora le differenze concrete tra uno scenario di riscaldamento globale di +1,5°C e +2°C rispetto ai livelli preindustriali. Analizzeremo gli impatti su ecosistemi, eventi estremi, risorse idriche e comunità umane.',
      objectives: [
        'Comprendere la differenza tra +1,5°C e +2°C',
        'Riconoscere gli impatti più comuni',
        'Collegare impatti e rischio locale',
      ],
      materials: [
        {
          title: 'Report IPCC - Sintesi per decisori',
          type: 'PDF • 18 pagine • 2.4 MB',
          size: '2.4 MB',
        },
        {
          title: 'Infografica - Impatti per livello di riscaldamento',
          type: 'PDF • 2 pagine • 850 KB',
          size: '850 KB',
        },
      ],
      videoPlaceholder:
        'https://www.climate.gov/sites/default/files/styles/full_width_620_original_image/public/IPCC1.5_extreme_heat_differences_lrg.png?itok=_tJ_R_o4',
    },
  },
  2: {
    id: 2,
    title: 'Casa efficiente: energia, bollette e comfort',
    subtitle: 'Corso attivo • 3 moduli',
    progressText: '1/3 moduli completati',
    modulesCompleted: 1,
    totalModules: 3,
    modules: [
      {
        id: 1,
        title: 'Riscaldamento e raffrescamento intelligenti',
        completed: 5,
        total: 5,
        status: 'completed',
        lessons: [
          {
            id: 1,
            title: 'Termostato: impostazioni che funzionano davvero',
            duration: '05:00',
            type: 'video',
            status: 'completed',
          },
          {
            id: 2,
            title: 'Dispersioni: dove si perde più calore',
            duration: '04:00',
            type: 'reading',
            status: 'completed',
          },
          {
            id: 3,
            title: '10 azioni low-cost per la casa',
            duration: '03:00',
            type: 'checklist',
            status: 'completed',
          },
          {
            id: 4,
            title: 'Cosa aumenta i consumi?',
            duration: '02:00',
            type: 'quiz',
            status: 'completed',
          },
          {
            id: 5,
            title: 'Crea il tuo piano di 3 azioni',
            duration: '03:00',
            type: 'exercise',
            status: 'completed',
          },
        ],
      },
      {
        id: 2,
        title: 'Elettricità e standby',
        completed: 1,
        total: 4,
        status: 'in-progress',
        lessons: [
          {
            id: 1,
            title: 'Standby: quanto incide?',
            duration: '04:00',
            type: 'video',
            status: 'completed',
          },
          {
            id: 2,
            title: 'Elettrodomestici: uso efficiente',
            duration: '04:00',
            type: 'reading',
            status: 'current',
          },
          {
            id: 3,
            title: 'Settimana senza sprechi in cucina',
            duration: '03:00',
            type: 'challenge',
            status: 'locked',
          },
          {
            id: 4,
            title: 'Quale gesto vale di più?',
            duration: '02:00',
            type: 'quiz',
            status: 'locked',
          },
        ],
      },
      {
        id: 3,
        title: "Acqua e qualità dell'aria indoor",
        completed: 0,
        total: 4,
        status: 'locked',
        lessons: [
          {
            id: 1,
            title: "Ridurre l'acqua senza rinunce",
            duration: '04:00',
            type: 'reading',
            status: 'locked',
          },
          {
            id: 2,
            title: 'Aerazione corretta in inverno/estate',
            duration: '04:00',
            type: 'video',
            status: 'locked',
          },
          {
            id: 3,
            title: 'Doccia, rubinetti, lavaggi: best practice',
            duration: '03:00',
            type: 'checklist',
            status: 'locked',
          },
          {
            id: 4,
            title: 'Errori comuni e alternative',
            duration: '03:00',
            type: 'quiz',
            status: 'locked',
          },
        ],
      },
    ],
    defaultLesson: {
      moduleId: 2,
      lessonId: 2,
      title: 'Elettrodomestici: uso efficiente',
      type: 'reading',
      description:
        "Questa lezione fornisce strategie pratiche per ottimizzare l'uso degli elettrodomestici più comuni, riducendo consumi e costi senza sacrificare comfort e funzionalità.",
      objectives: [
        'Identificare gli elettrodomestici più energivori',
        "Applicare best practice per l'uso efficiente",
        'Riconoscere quando sostituire un elettrodomestico obsoleto',
      ],
      materials: [
        {
          title: 'Guida - Etichette energetiche spiegate',
          type: 'PDF • 8 pagine • 1.2 MB',
          size: '1.2 MB',
        },
        {
          title: 'Checklist - Elettrodomestici efficienti',
          type: 'PDF • 3 pagine • 650 KB',
          size: '650 KB',
        },
      ],
      readingContent: `<h2>Elettrodomestici: uso efficiente</h2>

<p>Gli elettrodomestici rappresentano una quota significativa del consumo energetico domestico. <strong class="text-green-600">Frigorifero, lavatrice, lavastoviglie e forno</strong> sono tra i dispositivi che incidono maggiormente sulla bolletta elettrica. La buona notizia? Piccoli cambiamenti nelle abitudini quotidiane possono generare risparmi consistenti senza compromettere comfort o funzionalità.</p>

<h3>Frigorifero e congelatore: sempre accesi, sempre efficienti</h3>

<p>Il frigorifero è l'unico elettrodomestico che lavora 24 ore su 24, 365 giorni all'anno. Per questo motivo, anche piccole inefficienze si traducono in sprechi significativi nel lungo periodo.</p>

<p><strong class="text-green-600">Temperatura ottimale:</strong> imposta il frigorifero tra 3°C e 5°C, e il congelatore a -18°C. Temperature più basse aumentano i consumi senza benefici reali per la conservazione degli alimenti.</p>

<p><strong class="text-green-600">Posizionamento strategico:</strong> evita di collocare il frigorifero vicino a fonti di calore come fornelli, forno o radiatori. Lascia almeno 10 cm di spazio tra la parete e il retro dell'apparecchio per garantire una corretta ventilazione.</p>

<p><strong class="text-green-600">Manutenzione regolare:</strong> pulisci le serpentine posteriori almeno due volte l'anno. La polvere accumulata riduce l'efficienza del sistema di raffreddamento, aumentando i consumi fino al 30%. Sbrina il congelatore quando lo strato di ghiaccio supera i 5 mm.</p>

<p>Organizza il contenuto in modo razionale: un frigorifero troppo pieno ostacola la circolazione dell'aria fredda, mentre uno troppo vuoto disperde energia. L'ideale è riempirlo per circa il 70% della capacità.</p>

<h3>Lavatrice: meno gradi, più risparmio</h3>

<p>La lavatrice consuma energia principalmente per riscaldare l'acqua. <strong class="text-green-600">Un lavaggio a 30°C consuma circa il 60% in meno rispetto a uno a 60°C</strong>, e i moderni detersivi sono formulati per essere efficaci anche a basse temperature.</p>

<p><strong class="text-green-600">Carico completo:</strong> utilizza sempre la lavatrice a pieno carico. Se necessario, attiva la funzione di mezzo carico, ma ricorda che questa opzione non dimezza il consumo energetico: risparmierai circa il 30%.</p>

<p><strong class="text-green-600">Centrifuga intelligente:</strong> una centrifuga ad alta velocità (1200-1400 giri) riduce l'umidità residua nei tessuti, accorciando i tempi di asciugatura naturale o in asciugatrice. Il consumo aggiuntivo della centrifuga è minimo rispetto al risparmio ottenuto.</p>

<p>Evita il prelavaggio per i capi normalmente sporchi: questa fase consuma acqua ed energia senza apportare benefici significativi nella maggior parte dei casi. Riservala solo ai capi molto sporchi.</p>

<h3>Lavastoviglie: alleata dell'efficienza (se usata bene)</h3>

<p>Contrariamente a quanto si pensa, <strong class="text-green-600">la lavastoviglie consuma meno acqua ed energia rispetto al lavaggio a mano</strong>, a patto di utilizzarla correttamente.</p>

<p><strong class="text-green-600">Programma ECO:</strong> scegli sempre il programma economico. Anche se dura più a lungo, utilizza temperature più basse e consuma fino al 45% in meno di energia rispetto ai cicli intensivi. Per stoviglie normalmente sporche è più che sufficiente.</p>

<p><strong class="text-green-600">Caricamento ottimale:</strong> disponi piatti e pentole in modo da non ostacolare i getti d'acqua. Un caricamento corretto migliora l'efficacia del lavaggio e riduce la necessità di cicli aggiuntivi.</p>

<p>Rimuovi i residui di cibo grossolani prima del caricamento, ma evita il risciacquo preventivo sotto l'acqua corrente: è uno spreco inutile. I moderni detersivi e le lavastoviglie sono progettati per gestire residui leggeri.</p>

<h3>Forno: cottura efficiente e multitasking</h3>

<p>Il forno è uno degli elettrodomestici più energivori in cucina. Utilizzarlo in modo strategico può fare la differenza.</p>

<p><strong class="text-green-600">Preriscaldamento selettivo:</strong> il preriscaldamento è necessario solo per preparazioni specifiche come pane, pizza o dolci lievitati. Per la maggior parte delle pietanze (arrosti, verdure, lasagne) puoi inserire il cibo nel forno freddo e accenderlo: risparmierai fino a 20 minuti di consumo.</p>

<p><strong class="text-green-600">Cottura simultanea:</strong> sfrutta lo spazio del forno cuocendo più pietanze contemporaneamente. Utilizza la funzione ventilata per distribuire uniformemente il calore su più livelli.</p>

<p>Evita di aprire lo sportello durante la cottura: ogni apertura disperde fino al 20% del calore accumulato, costringendo il forno a consumare energia extra per ripristinare la temperatura.</p>

<p><strong class="text-green-600">Calore residuo:</strong> spegni il forno 5-10 minuti prima della fine della cottura e sfrutta il calore residuo per completare la preparazione. Questa tecnica funziona particolarmente bene per arrosti e gratinature.</p>

<h3>Quando sostituire un elettrodomestico</h3>

<p>Gli elettrodomestici moderni sono significativamente più efficienti rispetto ai modelli di 10-15 anni fa. <strong class="text-green-600">Un frigorifero di classe A+++ consuma fino al 60% in meno rispetto a un modello di classe A</strong> dello stesso volume.</p>

<p>Valuta la sostituzione quando:</p>

<ul>
<li>L'elettrodomestico ha più di 10 anni e appartiene a una classe energetica inferiore a A++</li>
<li>Le spese di riparazione superano il 50% del costo di un nuovo modello efficiente</li>
<li>Noti un aumento inspiegabile dei consumi elettrici</li>
</ul>

<p>Quando acquisti un nuovo elettrodomestico, <strong class="text-green-600">privilegia sempre la classe energetica più alta disponibile</strong>. Il costo iniziale leggermente superiore si ammortizza rapidamente grazie ai risparmi in bolletta.</p>

<h3>Riepilogo: azioni ad alto impatto</h3>

<p>Per massimizzare l'efficienza degli elettrodomestici, concentrati su queste azioni prioritarie:</p>

<ol>
<li><strong class="text-green-600">Lavatrici a 30°C:</strong> riduzione immediata del 60% dei consumi per il lavaggio</li>
<li><strong class="text-green-600">Programma ECO in lavastoviglie:</strong> risparmio del 45% rispetto ai cicli intensivi</li>
<li><strong class="text-green-600">Manutenzione frigorifero:</strong> pulizia serpentine e sbrinatura regolare</li>
<li><strong class="text-green-600">Forno multitasking:</strong> cottura simultanea e sfruttamento del calore residuo</li>
<li><strong class="text-green-600">Sostituzione strategica:</strong> priorità agli elettrodomestici più vecchi e inefficienti</li>
</ol>

<p>Ricorda: l'efficienza energetica non richiede sacrifici, ma solo una maggiore consapevolezza nell'uso quotidiano degli elettrodomestici. Piccoli gesti ripetuti nel tempo generano risparmi significativi e contribuiscono concretamente alla riduzione dell'impatto ambientale domestico.</p>`,
    },
  },
  3: {
    id: 3,
    title: 'Mobilità sostenibile: muoversi meglio, spendere meno',
    subtitle: 'Corso da iniziare • 3 moduli',
    progressText: '0/3 moduli completati',
    modulesCompleted: 0,
    totalModules: 3,
    modules: [
      {
        id: 1,
        title: 'Spostamenti quotidiani e alternative',
        completed: 0,
        total: 4,
        status: 'in-progress',
        lessons: [
          {
            id: 1,
            title: 'La gerarchia della mobilità sostenibile',
            duration: '05:00',
            type: 'video',
            status: 'current',
          },
          {
            id: 2,
            title: "Quando conviene davvero l'auto?",
            duration: '04:00',
            type: 'reading',
            status: 'locked',
          },
          {
            id: 3,
            title: 'Mappa il tuo tragitto più frequente',
            duration: '03:00',
            type: 'exercise',
            status: 'locked',
          },
          {
            id: 4,
            title: 'Spostamenti brevi: cosa cambia',
            duration: '02:00',
            type: 'quiz',
            status: 'locked',
          },
        ],
      },
      {
        id: 2,
        title: 'Bici, micromobilità e trasporto pubblico',
        completed: 0,
        total: 5,
        status: 'locked',
        lessons: [
          {
            id: 1,
            title: 'Come iniziare senza stress',
            duration: '05:00',
            type: 'video',
            status: 'locked',
          },
          {
            id: 2,
            title: 'Kit base e sicurezza',
            duration: '03:00',
            type: 'checklist',
            status: 'locked',
          },
          {
            id: 3,
            title: 'Qual è la scelta migliore qui?',
            duration: '02:00',
            type: 'quiz',
            status: 'locked',
          },
          {
            id: 4,
            title: '3 giorni senza auto (se possibile)',
            duration: '03:00',
            type: 'challenge',
            status: 'locked',
          },
          {
            id: 5,
            title: 'Barriere e soluzioni pratiche',
            duration: '03:00',
            type: 'diary',
            status: 'locked',
          },
        ],
      },
      {
        id: 3,
        title: "Viaggi e lavoro: ridurre l'impatto",
        completed: 0,
        total: 4,
        status: 'locked',
        lessons: [
          {
            id: 1,
            title: 'Treno vs aereo: differenze reali',
            duration: '04:00',
            type: 'reading',
            status: 'locked',
          },
          {
            id: 2,
            title: 'Smart working e impatto',
            duration: '04:00',
            type: 'video',
            status: 'locked',
          },
          {
            id: 3,
            title: 'Ottimizzare un viaggio',
            duration: '03:00',
            type: 'quiz',
            status: 'locked',
          },
          {
            id: 4,
            title: "Piano 'viaggio sostenibile' per il prossimo mese",
            duration: '03:00',
            type: 'exercise',
            status: 'locked',
          },
        ],
      },
    ],
    defaultLesson: {
      moduleId: 1,
      lessonId: 1,
      title: 'La gerarchia della mobilità sostenibile',
      type: 'video',
      description:
        'Questa lezione introduce il concetto di gerarchia della mobilità, uno strumento per valutare e scegliere le opzioni di trasporto più sostenibili in base al contesto e alle esigenze personali.',
      objectives: [
        'Comprendere la piramide della mobilità sostenibile',
        'Valutare le alternative di trasporto per i propri spostamenti',
        'Identificare opportunità di cambiamento realistiche',
      ],
      materials: [
        {
          title: 'Infografica - Gerarchia della mobilità',
          type: 'PDF • 1 pagina • 420 KB',
          size: '420 KB',
        },
        {
          title: 'Calcolatore - Impatto CO₂ per tipo di trasporto',
          type: 'Excel • 180 KB',
          size: '180 KB',
        },
      ],
      videoPlaceholder:
        'https://images.unsplash.com/photo-1571068316344-75bc76f77890?w=800&q=80',
    },
  },
}
